import { Directive, ElementRef, OnDestroy, Optional, Self } from '@angular/core';
import { DatePickerComponent, DateTimePickerComponent, TimePickerComponent } from '@progress/kendo-angular-dateinputs';
import { Collision, PopupRef } from '@progress/kendo-angular-popup';
import { Subscription } from 'rxjs';

// popupRef is public API on the date and time pickers but private on the datetime one, and
// reposition() is internal to the popup, so both are reached through these narrow types.
type PickerWithPopup = {
    open: { subscribe: (handler: () => void) => Subscription };
    popupRef: PopupRef;
};

type RepositionablePopup = {
    collision: Collision;
    reposition: () => void;
};

// The app header, which is pinned over the top of every page.
const FIXED_HEADER_SELECTOR = '.navbar.fixed-top';

// Kendo anchors a picker popup under the input and flips it above the input when there is not
// enough room below. When the popup fits neither below nor above - a field around the middle of
// the screen, or a tall datetime popup - the flip is skipped altogether (see the "skip flip"
// branch in @progress/kendo-popup-common/restrict-to-view) and the popup is left hanging off the
// bottom of the screen with its footer buttons unreachable. Kendo's collision handling has no
// fallback for that case, so this directive switches that one popup over to its "fit" collision,
// which keeps the popup inside the viewport and is recalculated by Kendo on every later
// scroll, resize and content change. Popups that do fit are left on the default flip behaviour.
// This covers every date and time field in the app because CommonModule exports the directive
// and every feature module imports CommonModule.
@Directive({
    selector: 'kendo-datepicker, kendo-datetimepicker, kendo-timepicker',
    standalone: false
})
export class PickerPopupViewportDirective implements OnDestroy {
    private picker: PickerWithPopup;
    private openSubscription: Subscription;

    constructor(
        @Optional() @Self() datePicker: DatePickerComponent,
        @Optional() @Self() dateTimePicker: DateTimePickerComponent,
        @Optional() @Self() timePicker: TimePickerComponent,
        private host: ElementRef<HTMLElement>
    ) {
        const picker = datePicker || dateTimePicker || timePicker;

        if (!picker) {
            return;
        }

        this.picker = picker as unknown as PickerWithPopup;
        this.openSubscription = this.picker.open.subscribe(() => this.onOpen());
    }

    ngOnDestroy(): void {
        this.openSubscription?.unsubscribe();
    }

    private onOpen(): void {
        // The open event is emitted before Kendo creates and places the popup, so the popup can
        // only be measured once the current task - which does both - has finished.
        Promise.resolve().then(() => this.adjustPopup());
    }

    private adjustPopup(): void {
        const popupRef = this.picker?.popupRef;

        // In adaptive mode the picker renders an action sheet instead of a popup.
        if (!popupRef?.popupElement) {
            return;
        }

        this.raiseAboveFixedHeader(popupRef.popupElement);
        this.fitPopupIfItDoesNotFlip(popupRef);
    }

    // On a plain page nothing above the input carries a z-index, so Kendo leaves the popup on the
    // theme default of 100 while the app header is a Bootstrap .fixed-top at 1030 - a popup
    // flipped above a field low on the page then has its date/time tabs painted over by the
    // header. Inside a dialog Kendo already derives a higher z-index from the anchor's ancestors,
    // which is why the same field behaves in a modal, so this only ever raises, never lowers.
    private raiseAboveFixedHeader(container: HTMLElement): void {
        const header = document.querySelector<HTMLElement>(FIXED_HEADER_SELECTOR);

        if (!header) {
            return;
        }

        const headerZIndex = parseInt(window.getComputedStyle(header).zIndex, 10);
        const popupZIndex = parseInt(window.getComputedStyle(container).zIndex, 10);

        if (!headerZIndex || popupZIndex > headerZIndex) {
            return;
        }

        container.style.zIndex = String(headerZIndex + 1);
    }

    private fitPopupIfItDoesNotFlip(popupRef: PopupRef): void {
        const anchor = this.host.nativeElement.getBoundingClientRect();
        const popupHeight = popupRef.popupElement.getBoundingClientRect().height;
        const roomBelow = window.innerHeight - anchor.bottom;
        const roomAbove = anchor.top;

        if (!popupHeight || popupHeight <= roomBelow || popupHeight <= roomAbove) {
            return;
        }

        const popup = popupRef.popup.instance as unknown as RepositionablePopup;

        popup.collision = { ...popup.collision, vertical: 'fit' };
        popup.reposition();
    }
}
